import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';

export type Entity = {
  id: number;
  name: string;
  type: string;
  color: string | null;
  icon: string | null;
  access_level: 'leitura' | 'escrita' | 'total';
  can_see_private: boolean;
};

export type AuthUser = {
  id: number;
  name: string;
  email: string;
  locale: string;
  timezone: string;
  theme: string;
  roles: string[];
  permissions: string[];
  entities: Entity[];
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Recarrega o usuario a partir de `/auth/me`.
   *
   * O payload traz as ENTIDADES ACESSIVEIS, e o seletor de contexto da barra
   * superior se alimenta dele. Sem isso, cadastrar uma entidade nova nao a faz
   * aparecer no seletor: o dado veio do login e ninguem o atualizou. O usuario
   * cria a entidade, ve a mensagem de sucesso, e o seletor continua vazio —
   * parece que nao funcionou.
   *
   * Chamada tambem quando o acesso a uma entidade muda.
   */
  refreshUser: () => Promise<void>;
  /** Aceita curinga concedido: `fin.*.*` autoriza `fin.transactions.create`. */
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restaura a sessao na primeira carga. O 401 aqui e esperado quando nao ha
  // sessao: nao e erro, e a resposta correta para "ninguem esta logado".
  useEffect(() => {
    let cancelled = false;

    api
      .get<{ data: AuthUser }>('/auth/me')
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch((error: unknown) => {
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error('Falha ao restaurar sessao:', error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ data: AuthUser }>('/auth/login', { email, password });
    setUser(res.data);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get<{ data: AuthUser }>('/auth/me');
      setUser(res.data);
    } catch (error) {
      // Falha aqui nao pode derrubar a sessao: o usuario continua com o que
      // ja tinha carregado, que e melhor que uma tela vazia.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Falha ao recarregar o usuario:', error);
      }
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Limpa o estado local mesmo se a chamada falhar: manter o usuario
      // "logado" na interface depois de um logout e pior que um erro visivel.
      setUser(null);
    }
  }, []);

  const can = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      if (user.permissions.includes(permission)) return true;

      return user.permissions.some((granted) => {
        if (!granted.includes('*')) return false;
        const regex = new RegExp(`^${granted.replace(/\./g, '\\.').replace(/\*/g, '[^.]+')}$`);
        return regex.test(permission);
      });
    },
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser, can }),
    [user, loading, login, logout, refreshUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return context;
}
